// Always-mounted Task modal header: projection-derived lifecycle/live labels plus display-only controls.

import { Icon } from './Icon.tsx';
import type {
  TaskDetailProjection,
  TaskDetailState,
  TaskLiveState,
} from '../surfaces/know-projections.ts';

const STATE_PILL: Readonly<Record<TaskDetailState, string>> = {
  draft: 'm-state draft',
  briefed: 'm-state warn',
  working: 'm-state',
  blocked: 'm-state warn',
  done: 'm-state',
  dropped: 'm-state draft',
};

const STATE_LABEL: Readonly<Record<TaskDetailState, string>> = {
  draft: 'Draft',
  briefed: 'Briefed',
  working: 'Working',
  blocked: 'Blocked',
  done: 'Done',
  dropped: 'Dropped',
};

export const TASK_LIVE_LABEL: Readonly<Record<TaskLiveState, string>> = {
  agent_working: 'Agent working',
  waiting: 'Waiting',
  idle: 'Idle',
};

/** The live label beside the pill only when it ADDS something: "Working · Agent working" said the same
 *  thing twice, and "Idle" beside a lifecycle pill is noise. The rail's Now card always carries it. */
function showLiveMeta(detail: TaskDetailProjection): boolean {
  if (detail.liveState === 'idle') return false;
  return !(detail.liveState === 'agent_working' && detail.state === 'working');
}

export interface TaskModalTopProps {
  readonly detail: TaskDetailProjection | null;
  readonly chatHidden: boolean;
  readonly onToggleChat: () => void;
  readonly onClose: () => void;
}

export function TaskModalTop({
  detail,
  chatHidden,
  onToggleChat,
  onClose,
}: TaskModalTopProps): React.JSX.Element {
  return (
    <div className="m-top">
      {detail !== null ? (
        <>
          <span className={STATE_PILL[detail.state]}>
            <span className="dot" aria-hidden="true" /> {STATE_LABEL[detail.state]}
          </span>
          {showLiveMeta(detail) ? (
            <span className="m-meta">{TASK_LIVE_LABEL[detail.liveState]}</span>
          ) : null}
        </>
      ) : null}
      <span className="spacer" />
      <button type="button" className="m-toggle" aria-pressed={chatHidden} onClick={onToggleChat}>
        {chatHidden ? 'Show chat' : 'Hide chat'}
      </button>
      <button type="button" className="m-x" aria-label="Close task" onClick={onClose}>
        <Icon name="x" size={15} />
      </button>
    </div>
  );
}
